import {
  AnalyticEventType,
  fireAnalyticEvent,
  MockHook,
  isAQuery,
  QueryV1Input,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import { Question } from '@jupiterone/jupiter-types';
import { useState, MouseEvent, useEffect } from 'react';
import { useArrayState } from 'react-use-object-state';
import { suggest } from '@jupiterone/query-language-autocomplete';
import j1QueryToQueryOutput from './j1QueryToQueryOutput';

export interface QueryOutput {
  type: 'query';
  runTimestamp: number;
  query: QueryV1Input;
}

export interface QuestionOutput {
  type: 'question';
  runTimestamp: number;
  question: Question;
}
export type Search = QueryOutput | QuestionOutput;

export interface SearchAnywhereContainerProps {
  searchAnywhereOpen: boolean;
  handleSearchAnywhereDisable: VoidFunction;
}
const useSearchAnywhereLogic = ({
  searchAnywhereOpen,
  handleSearchAnywhereDisable,
}: SearchAnywhereContainerProps) => {
  const searches = useArrayState<Search>([]);
  const [searchText, setSearchText] = useState<string>('');
  const notification = useSnackbarNotification();

  const handleNoResultBackgroundClick = (e: MouseEvent) => {
    e.preventDefault();
    return (!searches.state || searches.state.length === 0) && handleClose();
  };

  const isFocusable = (
    element: HTMLElement | Element
  ): element is HTMLElement => {
    return (element as HTMLElement).focus !== undefined;
  };

  useEffect(() => {
    if (searchAnywhereOpen) {
      const target = document?.getElementsByClassName('ace_text-input');
      if (target !== null && target[0] !== null && isFocusable(target[0])) {
        target[0].focus();
      }
    }
  }, [searchAnywhereOpen]);

  const handleNewSearch = (searchQuery: string, includeDeleted?: boolean) => {
    searches.clear();
    setSearchText(searchQuery);
    try {
      if (isAQuery(searchQuery)) {
        suggest(searchQuery);
      }
      searches.unshift(
        j1QueryToQueryOutput({
          query: searchQuery,
          remember: true,
          includeDeleted: includeDeleted || false,
        })
      );
    } catch (err) {
      notification.error({
        message: err.message,
        error: err,
      });
    }

    fireAnalyticEvent({ type: AnalyticEventType.LANDING_QUERY });
  };

  const handleAddQuestion = (question: QuestionOutput['question']) => {
    try {
      searches.unshift({
        type: 'question',
        question,
        runTimestamp: new Date().getTime(),
      });
    } catch (err) {
      notification.error({
        message: err.message,
        error: err,
      });
    }
  };

  const handleClose = () => {
    searches.clear();
    handleSearchAnywhereDisable();
  };

  return {
    searches,
    searchAnywhereOpen,
    handleClose,
    handleNewSearch,
    searchText,
    handleAddQuestion,
    handleClearSearchList: searches.clear,
    handleNoResultBackgroundClick,
  };
};

export type SearchAnywhereContainerComponentProps = ReturnType<
  typeof useSearchAnywhereLogic
>;
export const MOCK_SEARCH_ANYWHERE_PROPS: SearchAnywhereContainerComponentProps = {
  searchAnywhereOpen: MockHook.mockFunction,
  handleAddQuestion: MockHook.mockFunction,
  handleClose: MockHook.mockFunction,
  handleNewSearch: MockHook.mockFunction,
  searchText: '',
  handleClearSearchList: MockHook.mockFunction,
  handleNoResultBackgroundClick: MockHook.mockFunction,

  searches: { state: [] } as any,
};
export default MockHook.useMockHook(
  useSearchAnywhereLogic,
  MOCK_SEARCH_ANYWHERE_PROPS
);
